import { useEffect, useRef } from 'react';
import { PERFORMANCE_METRIC_EVENTS } from '../../constants/performance';
import { usePerformanceMetrics } from './use-performance-metrics';

/**
 * Measures per-session dropped frame ratio. Multi-video ready.
 */
export function usePlaybackQualityMetrics(videoElement: HTMLVideoElement | null) {
  const {
    captureGauge
  } = usePerformanceMetrics();
  const captureGaugeRef = useRef(captureGauge);
  // eslint-disable-next-line react-hooks/refs
  captureGaugeRef.current = captureGauge;
  const playStartDroppedRef = useRef(0);
  const playStartTotalRef = useRef(0);
  useEffect(() => {
    if (!videoElement) return;
    const handlePlay = () => {
      if (typeof videoElement.getVideoPlaybackQuality === 'function') {
        const quality = videoElement.getVideoPlaybackQuality();
        playStartDroppedRef.current = quality.droppedVideoFrames;
        playStartTotalRef.current = quality.totalVideoFrames;
      }
    };
    const handlePause = () => {
      if (typeof videoElement.getVideoPlaybackQuality === 'function') {
        const quality = videoElement.getVideoPlaybackQuality();
        const sessionDropped = quality.droppedVideoFrames - playStartDroppedRef.current;
        const sessionTotal = quality.totalVideoFrames - playStartTotalRef.current;
        if (sessionTotal > 0) {
          captureGaugeRef.current({
            name: PERFORMANCE_METRIC_EVENTS.PLAYBACK_DROPPED_FRAME_RATIO,
            value: sessionDropped / sessionTotal * 100,
            tags: {
              totalVideoFrames: sessionTotal,
              droppedVideoFrames: sessionDropped
            }
          });
        }
      }
    };
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    return () => {
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
    };
  }, [videoElement]);
}